import React from 'react';
import type {
  ComponentRendering,
  ImageField,
  RichTextField,
  TextField,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';
import { ComponentProps } from 'src/jss/lib/component-props';

import { SitecoreRichText, SitecoreText } from '@alliander-fe/jss-utils';
import { Image } from 'src/components/Image';
import { ScrollLinkWrapper, Skeleton, Stack } from '@ads-core/components';
import { ImageGallery as ImageGalleryComponent } from '@ads-core/components';

import { AdditionalFields } from 'src/components/TableOfContentsContainer/types/TableOfContentsComponentRendering';
import { useIntersection } from 'src/hooks/useIntersection';
import { useDynamicThreshold } from 'src/hooks/useDynamicThreshold';

type ImageProps = ImageField & {
  value: {
    alt?: string;
    width?: number;
    height?: number;
  };
};

type ImagesProps = {
  id: string;
  url: string;
  name: string;
  displayName: string;
  fields: {
    description: TextField;
    title: TextField;
    image: ImageProps;
  };
};
export type ImageGalleryProps = ComponentProps &
  AdditionalFields & {
    fields: {
      images: ImagesProps[];
      text: RichTextField;
      title: TextField;
    };
    params?: {
      anchor?: string;
    };
    rendering: ComponentRendering & AdditionalFields;
  };

const ImageGallery = (props: ImageGalleryProps) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const additionalFields = props.rendering.additionalFields;
  const threshold = useDynamicThreshold({ ref });

  // The intersection observer is used to set the intersecting state.
  // This way we can set the table of content item as active.
  useIntersection({
    callBack: (isIntersecting: boolean) => {
      if (isIntersecting && additionalFields?.anchor) {
        additionalFields?.isIntersecting(additionalFields?.anchor ?? '');
      }
    },
    ref,
    threshold: threshold,
  });

  const gallery = React.useMemo(
    () =>
      props.fields?.images
        .map((image) => {
          const imageData = image?.fields?.image?.value;
          const alt = imageData?.alt ?? '';
          const width = imageData?.width ?? 0;
          const height = imageData?.height ?? 0;

          if (!imageData) return;

          return {
            id: image.id,
            image: (
              <Stack isFullHeight isFullWidth asChild>
                <Image
                  src={imageData.src ?? ''}
                  alt={alt}
                  width={width}
                  height={height}
                  placeholder={<Skeleton.Item width="100%" height="100%" />}
                />
              </Stack>
            ),
            title: <SitecoreText field={image.fields.title} editable />,
            description: <SitecoreText field={image.fields.description} editable />,
          };
        })
        .filter((item) => item !== undefined),
    [props.fields?.images]
  );

  if (!props.fields) return null;

  return (
    <div ref={ref}>
      <ScrollLinkWrapper anchorId={additionalFields?.anchor ?? props.params?.anchor ?? undefined}>
        <ImageGalleryComponent
          title={<SitecoreText field={props.fields.title} editable />}
          text={<SitecoreRichText field={props.fields.text} editable />}
          gallery={gallery}
          isContained={Boolean(additionalFields?.isContained)}
        />
      </ScrollLinkWrapper>
    </div>
  );
};

export default withDatasourceCheck()<ComponentProps>(ImageGallery);
